import { useState, type FormEvent } from 'react';
import { projectTypes } from '@/mocks/services';

const FORM_URL = 'https://readdy.ai/api/form/daqag2ehq9f5fk98ckg0';
const MAX_MSG = 500;

type Errors = Partial<Record<'name' | 'email' | 'message', string>>;

const budgets = ['Under €1,000', '€1,000–€2,500', '€2,500–€5,000', '€5,000+', 'Let\'s discuss'];

const fieldCls = (err?: string) => `mt-2.5 h-11 w-full border-0 border-b bg-transparent px-0 py-2 text-[15px] text-foreground-950 placeholder:text-foreground-500 outline-none transition-colors ${err ? 'border-foreground-950' : 'border-background-200'}`;
const line = <span className="mo-field__line" aria-hidden="true" />;
const labelCls = 'block font-label text-[11px] font-medium uppercase tracking-[0.14em] text-foreground-500';

export default function InquiryForm() {
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [msgLen, setMsgLen] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const aux = String(data.get('website_alt') ?? '').trim();
    if (aux) { setStatus('success'); return; }
    data.delete('website_alt');

    const name = String(data.get('name') ?? '').trim();
    const email = String(data.get('email') ?? '').trim();
    const message = String(data.get('message') ?? '').trim();
    const next: Errors = {};
    if (!name) next.name = 'Please enter your name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = 'Please enter a valid email address.';
    if (!message) next.message = 'Please tell me a little about the project.';
    else if (message.length > MAX_MSG) next.message = `Please keep it under ${MAX_MSG} characters.`;
    setErrors(next);
    if (Object.keys(next).length) return;

    setStatus('sending');
    setFormError('');
    try {
      const body = new URLSearchParams();
      data.forEach((v, k) => body.append(k, String(v)));
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (response.ok && parsed?.code === 'OK' && !/spam/i.test(String(serverMsg || ''))) {
        setStatus('success');
        form.reset();
        setMsgLen(0);
      } else {
        setFormError(serverMsg ? String(serverMsg) : 'Something went wrong. Please try again.');
        setStatus('error');
      }
    } catch {
      setFormError('Network error — please check your connection and try again.');
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="border-t border-background-200 pt-8" role="status" aria-live="polite">
        <p className="font-label text-[11px] uppercase tracking-[0.18em] text-foreground-700">Inquiry received</p>
        <h3 className="mt-4 font-heading text-2xl font-medium tracking-[-0.015em] text-foreground-950">Thank you — I&apos;ll be in touch within two working days.</h3>
        <button type="button" onClick={() => setStatus('idle')} className="mt-8 border-b border-foreground-950 pb-1 font-label text-[11px] uppercase tracking-[0.18em] text-foreground-950 hover:opacity-60 transition-opacity whitespace-nowrap cursor-pointer">
          Send another inquiry
        </button>
      </div>
    );
  }

  return (
    <form id="inquiry-form" data-readdy-form noValidate onSubmit={handleSubmit} className="relative grid grid-cols-1 gap-x-10 gap-y-8 sm:grid-cols-2">
      <div className="inq-aux-row">
        <label htmlFor="website_alt">Website</label>
        <input id="website_alt" type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly onFocus={(ev) => ev.currentTarget.removeAttribute('readonly')} />
      </div>
      <div className="mo-fieldset">
        <label htmlFor="inq-name" className={labelCls}>Name *</label>
        <div className="mo-field"><input id="inq-name" name="name" type="text" autoComplete="name" aria-invalid={!!errors.name} aria-describedby={errors.name ? 'err-name' : undefined} className={fieldCls(errors.name)} placeholder="Your name" />{line}</div>
        {errors.name && <p id="err-name" className="mt-1.5 text-xs text-foreground-950">— {errors.name}</p>}
      </div>
      <div className="mo-fieldset">
        <label htmlFor="inq-email" className={labelCls}>Email *</label>
        <div className="mo-field"><input id="inq-email" name="email" type="email" autoComplete="email" aria-invalid={!!errors.email} aria-describedby={errors.email ? 'err-email' : undefined} className={fieldCls(errors.email)} placeholder="name@company.com" />{line}</div>
        {errors.email && <p id="err-email" className="mt-1.5 text-xs text-foreground-950">— {errors.email}</p>}
      </div>
      <div className="mo-fieldset">
        <label htmlFor="inq-company" className={labelCls}>Company / Brand</label>
        <div className="mo-field"><input id="inq-company" name="company" type="text" autoComplete="organization" className={fieldCls()} placeholder="Optional" />{line}</div>
      </div>
      <div className="mo-fieldset">
        <label htmlFor="inq-type" className={labelCls}>Project Type</label>
        <div className="mo-field relative">
          <select id="inq-type" name="projectType" defaultValue="" className={`${fieldCls()} appearance-none pr-10 cursor-pointer`}>
            <option value="" disabled>Select a type</option>
            {projectTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <span className="mo-select-arrow pointer-events-none absolute right-3 top-1/2 flex h-4 w-4 items-center justify-center text-foreground-600"><i className="ri-arrow-down-s-line" /></span>{line}
        </div>
      </div>
      <div className="mo-fieldset">
        <label htmlFor="inq-budget" className={labelCls}>Estimated Budget</label>
        <div className="mo-field relative">
          <select id="inq-budget" name="budget" defaultValue="" className={`${fieldCls()} appearance-none pr-10 cursor-pointer`}>
            <option value="" disabled>Select a range</option>
            {budgets.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <span className="mo-select-arrow pointer-events-none absolute right-3 top-1/2 flex h-4 w-4 items-center justify-center text-foreground-600"><i className="ri-arrow-down-s-line" /></span>{line}
        </div>
      </div>
      <div className="mo-fieldset">
        <label htmlFor="inq-date" className={labelCls}>Timeline / Shoot Date</label>
        <div className="mo-field"><input id="inq-date" name="timeline" type="text" className={fieldCls()} placeholder="e.g. Early March, or flexible" />{line}</div>
      </div>
      <div className="mo-fieldset sm:col-span-2">
        <label htmlFor="inq-msg" className={labelCls}>What&apos;s the project? *</label>
        <div className="mo-field flex"><textarea id="inq-msg" name="message" rows={6} maxLength={MAX_MSG} onChange={(ev) => setMsgLen(ev.target.value.length)} aria-invalid={!!errors.message} aria-describedby={errors.message ? 'err-msg' : undefined} className={`${fieldCls(errors.message)} !h-auto min-h-[160px] resize-none`} placeholder="Brand, concept, usage, timeline, budget range…" />{line}</div>
        <div className="mt-1.5 flex justify-between gap-4 text-xs">
          <span id="err-msg" className="text-foreground-950">{errors.message ? `— ${errors.message}` : ''}</span>
          <span className={`mo-counter font-label text-foreground-500 ${msgLen > 0 ? 'is-typing' : ''}`}>{msgLen}/{MAX_MSG}</span>
        </div>
      </div>
      {status === 'error' && formError && (
        <p role="alert" className="text-sm font-medium text-foreground-950 sm:col-span-2">{formError}</p>
      )}
      <div className="-mt-2 sm:col-span-2">
        <button type="submit" disabled={status === 'sending'} className="mo-btn mo-btn--submit inline-flex w-full items-center justify-center gap-2 bg-foreground-950 px-8 py-4 font-label text-[12px] font-medium uppercase tracking-[0.14em] text-background-50 hover:opacity-90 disabled:opacity-60 sm:w-auto whitespace-nowrap cursor-pointer">
          {status === 'sending' ? 'Sending…' : 'Send inquiry'} <span aria-hidden="true" className="mo-arrow">→</span>
        </button>
      </div>
    </form>
  );
}